import { FC, memo, useCallback } from 'react';

import { IModifierData } from 'features/menu/components/product-detail/modifier/types';
import { SelectModifierFn } from 'features/menu/components/product-detail/product-customization/types';

import { NUM_COLUMNS } from 'components/list-card-group/styled';
import { ListCardGroupVariant } from 'components/list-card-group/types';
import { StaticCardGrid } from 'components/product-customization/static-layout';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';

import { Modifier } from '../modifier/modifier';

export interface IModifierGroup {
  key: string;
  displayGroupName?: TranslationKey;
  modifiers: IModifierData[];
}

export type IModifierGroupProps = IModifierGroup & {
  onModifierSelectionChange: SelectModifierFn;
  isFullContainer?: boolean;
};

interface ModifierGridItemProps {
  modifierProps: IModifierData;
  onModifierSelectionChange: SelectModifierFn;
}

const ModifierGridItem = memo(function ModifierGridItem({
  modifierProps,
  onModifierSelectionChange,
}: ModifierGridItemProps) {
  const handleSelectionsChange = useCallback(
    (selections: IModifierData['selections']) => {
      onModifierSelectionChange(selections, modifierProps.modifier);
    },
    [modifierProps.modifier, onModifierSelectionChange]
  );

  return (
    <Modifier
      key={`modifier-${modifierProps.modifier.id}`}
      modifier={modifierProps.modifier}
      selections={modifierProps.selections}
      onSelectionsChange={handleSelectionsChange}
    />
  );
});

export const ModifierGroup: FC<IModifierGroupProps> = memo(function ModifierGroup({
  displayGroupName,
  modifiers,
  onModifierSelectionChange,
  isFullContainer = false,
}) {
  return (
    <StaticCardGrid
      numColumns={NUM_COLUMNS}
      isFullContainer={isFullContainer}
      listCardGroupVariant={ListCardGroupVariant.GROUP}
      header={displayGroupName ? t(displayGroupName) : undefined}
      items={modifiers}
      renderItem={({ item: modifierProps }) => (
        <ModifierGridItem
          key={`modifier-${modifierProps.modifier.id}`}
          modifierProps={modifierProps}
          onModifierSelectionChange={onModifierSelectionChange}
        />
      )}
    />
  );
});
